<template>
    <section
        class="carousel-section"
        :class="carouselClasses"
        :style="`background-color: ${blok?.BackgroundColor?.color};`"
    >
        <div :class="{ 'full-width': blok.full_width, 'has-pagination': blok.show_pagination }">
            <LazyClientOnly v-if="blok.items.length">
                <carousel
                    ref="imagesCarousel"
                    :transition="500"
                    :items-to-show="getItemsToShow"
                    :wrap-around="blok.infinity_scroll"
                    :autoplay="blok.autoplay ? blok.autoplay : 0"
                    snap-align="start"
                    class="component-carousel"
                    :class="{ 'is-centered': blok.items?.length <= getItemsToShow }"
                    :style="carouselContainerStyles"
                >
                    <slide v-for="component in blok.items" :key="component._uid" :style="slideStyles">
                        <NuxtImg
                            v-if="component.component === 'ImageTmp' && component.image.filename"
                            :src="component.image.filename"
                            :title="component.image.title"
                            :alt="component.image.alt"
                            provider="storyblok"
                            format="webp"
                            loading="lazy"
                            densities="x1"
                        />
                        <Component :is="component.component" :blok="component" class="w-full" />
                    </slide>

                    <template #addons>
                        <navigation v-if="getItemsToShow && blok.items?.length > getItemsToShow">
                            <template #prev>
                                <NuxtIcon name="ri:arrow-left-line" />
                            </template>
                            <template #next>
                                <NuxtIcon name="ri:arrow-right-line" />
                            </template>
                        </navigation>
                        <Pagination v-if="blok.show_pagination" />
                    </template>
                </carousel>
            </LazyClientOnly>
            <div v-if="blok.caption" class="caption">
                {{ blok.caption }}
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { ICarousel } from '~ui/types';
import 'vue3-carousel/dist/carousel.css';
// @ts-ignore
import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel';
import screens from '#tailwind-config/theme/screens';
import { CarouselBtnStyles } from '~ui/types/enums/CarouselBtnStyles';

const { blok } = defineProps<{ blok: ICarousel }>();

const imagesCarousel: any = ref(null);

const screenMd = Number(screens.md.slice(0, -2));
const screenLg = Number(screens.lg.slice(0, -2));
const screenXl = Number(screens.xl.slice(0, -2));
const innerWidth = ref(1000);

const carouselClasses = computed(() => ({
    'navigation-square': blok.slideNavigationBtnStyle === CarouselBtnStyles.SQUARE,
    'navigation-circle': blok.slideNavigationBtnStyle === CarouselBtnStyles.CIRCLE,
}));

const slideStyles = computed(() => {
    const slideStyle = {};
    if (blok.slideGap) {
        slideStyle['padding-left'] = `${blok.slideGap / 2}px`;
        slideStyle['padding-right'] = `${blok.slideGap / 2}px`;
    }

    return slideStyle;
});

const carouselContainerStyles = computed(() => {
    const carouselContainerStyle = {
        '--next-btn-right-position': '-1rem',
        '--prev-btn-left-position': '-1rem',
        '--navigation-offset': blok.slideNavigationBtnOffset ? `${blok.slideNavigationBtnOffset}px` : null,
    };
    if (blok.slideGap) {
        const btnPosition = parseInt(blok.slideGap) === 0 ? 0 : blok.slideGap / 2 - 20;
        carouselContainerStyle['--carousel-section-margin'] = `${(blok.slideGap / 2) * -1}px`;
        carouselContainerStyle['--next-btn-right-position'] = `${btnPosition}px`;
        carouselContainerStyle['--prev-btn-left-position'] = `${btnPosition}px`;
    }

    return carouselContainerStyle;
});

onActivated(() => {
    innerWidth.value = window.innerWidth + 1;
    innerWidth.value = window.innerWidth - 1;
});

/** useCurrentElement used to get HTML element of carousel as this ref returns only Vue instance
 * useElementSize used to track the width of carousel and restart it if display none was applied to carousel or parent
 */
const { width: isCarouselVisible } = useElementSize(useCurrentElement(imagesCarousel.value));
watch(isCarouselVisible, (newValue, oldValue) => {
    // Check oldValue here to restart carousel only if width was 0 and prevent restart on resize
    if (newValue && !oldValue) {
        nextTick(() => {
            imagesCarousel.value?.restartCarousel();
        });
    }
});

function onResize() {
    innerWidth.value = window.innerWidth;
}

onMounted(() => {
    window.addEventListener('resize', onResize);
    onResize();
});
onUnmounted(() => window.removeEventListener('resize', onResize));

const getItemsToShow = computed(() => {
    if (innerWidth.value >= screenXl) {
        return parseInt(blok.item_count_xl_screen);
    } else if (innerWidth.value < screenXl && innerWidth.value >= screenLg) {
        return parseInt(blok.item_count_lg_screen);
    } else if (innerWidth.value < screenLg && innerWidth.value >= screenMd) {
        return parseInt(blok.item_count_md_screen);
    } else {
        return parseInt(blok.item_count_sm_screen);
    }
});
</script>

<style scoped lang="postcss">
.full-width {
    @apply w-full max-w-full m-0;
    :deep(.carousel) {
        .carousel__next {
            @apply right-2 left-auto translate-x-0;
        }

        .carousel__prev {
            @apply left-2 translate-x-0;
        }
    }
}
.container.has-pagination {
    :deep(.carousel) {
        .carousel__next,
        .carousel__prev {
            @apply translate-y-[calc(-50%_-_16px)];
        }
    }
}

.component-carousel {
    &.is-centered {
        :deep(.carousel__track) {
            @apply justify-center;
        }
    }
    &__image {
        @apply block object-contain object-center px-3;
    }
}

.carousel-section {
    @apply px-4 pt-6 pb-10;
    @apply md:pt-10 md:pb-16 md:mx-[var(--carousel-section-margin)];
    &.navigation-circle {
        :deep(.carousel) {
            .carousel__next,
            .carousel__prev {
                @apply rounded-[50%];
            }
        }
    }
    &__title {
        @apply text-center mb-12 text-base md:text-xl;
    }
    :deep(.carousel) {
        &__slide {
            @apply md:px-1;
            align-items: unset;
        }
        .carousel__next,
        .carousel__prev {
            @apply h-10 w-10 text-lg rounded-full bg-white transition-all -translate-y-1/2 text-[#0094FF] mx-0;
            @apply xl:w-12 xl:h-12 xl:text-2xl;

            box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.11);
            top: calc(50% + var(--navigation-offset, 0px));
        }
        .carousel__next--disabled,
        .carousel__prev--disabled {
            @apply opacity-0 pointer-events-none;
        }
        .carousel__pagination {
            @apply flex mt-6;
        }
        .carousel__pagination-button {
            &::after {
                @apply w-2 h-2 rounded-full bg-gray-400 opacity-25;
            }
        }
        .carousel__pagination-button--active,
        .carousel__pagination-button:hover {
            &::after {
                @apply bg-[#0094FF] opacity-75;
            }
        }
    }
}

:deep(.carousel) {
    .carousel__next {
        @apply right-[var(--next-btn-right-position)];
    }
    .carousel__prev {
        @apply left-[var(--prev-btn-left-position)];
    }
}

.caption {
    @apply text-center text-sm text-gray-800 mt-3 mb-1;
}
</style>
